import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { MagnifyingGlass, Storefront, ArrowRight, MapPin, Tag } from "@phosphor-icons/react";
import { cloudyLogo } from "../lib/cloudy";

/** Live business-name autocomplete. Debounced, keyboard-navigable, glass-style dropdown. */
export default function AutocompleteSearch({ placeholder = "Tapez le nom de votre entreprise…", compact = false, autoFocus = false, fallbackPath = "/annuaire" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setCities([]); setCategories([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/public/autocomplete", { params: { q: q.trim(), limit: 8 } });
        setResults(r.data.businesses || []);
        setCities(r.data.cities || []);
        setCategories(r.data.categories || []);
        setOpen(true);
      } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const go = (biz) => { setOpen(false); setQ(""); navigate(`/entreprise/${biz.slug}`); };
  const goCity = (city) => { setOpen(false); setQ(""); navigate(`${fallbackPath}?city=${encodeURIComponent(city)}`); };
  const goCategory = (cat) => { setOpen(false); setQ(""); navigate(`${fallbackPath}?category=${encodeURIComponent(cat)}`); };

  const submit = () => {
    if (!q.trim()) return;
    if (results.length > 0) { go(results[0]); return; }
    setOpen(false);
    navigate(`${fallbackPath}?q=${encodeURIComponent(q.trim())}`);
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (cursor >= 0 && results[cursor]) { go(results[cursor]); return; }
      submit();
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Escape") setOpen(false);
  };

  const hasAny = results.length > 0 || cities.length > 0 || categories.length > 0;

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="autocomplete-search">
      <div className={`flex items-center gap-2 rounded-sm bg-card border ${open ? "border-primary shadow-[0_0_0_4px_rgba(19,78,52,0.08)]" : "border-border"} transition-all ${compact ? "p-1" : "p-1.5"}`}>
        <button
          type="button"
          onClick={submit}
          aria-label="Lancer la recherche"
          className="pl-3 pr-1 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          data-testid="autocomplete-submit"
        >
          <MagnifyingGlass size={compact ? 16 : 20} weight="bold"/>
        </button>
        <input
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => { setQ(e.target.value); setCursor(-1); }}
          onFocus={() => q.length >= 2 && setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className={`flex-1 min-w-0 bg-transparent outline-none ${compact ? "px-1 py-2 text-sm" : "px-2 py-3 text-base"}`}
          data-testid="autocomplete-input"
        />
        {q.trim() && (
          <button
            type="button"
            onClick={submit}
            className={`${compact ? "text-xs px-3 py-1.5" : "text-sm px-4 py-2"} bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white rounded-sm font-semibold transition-colors shrink-0 mr-1`}
            data-testid="autocomplete-search-btn"
          >
            Rechercher
          </button>
        )}
        {loading && <div className="pr-3 text-xs text-muted-foreground animate-pulse">…</div>}
      </div>

      {open && hasAny && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-card/95 backdrop-blur-xl border border-border rounded-sm shadow-[0_24px_48px_-12px_rgba(19,78,52,0.18)] overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="autocomplete-dropdown">
          {results.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold px-4 pt-3 pb-1.5">Entreprises</div>
              {results.map((b, i) => {
                const isPremium = b.premium && (b.premium.tier === "premium" || b.premium.tier === "premium_ai");
                return (
                  <button
                    key={b.id}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => go(b)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b border-border/50 ${i === cursor ? "bg-accent/60" : "hover:bg-accent/40"}`}
                    data-testid={`autocomplete-result-${b.slug}`}
                  >
                    <div className="w-9 h-9 shrink-0 bg-primary/10 text-primary grid place-items-center rounded-sm">
                      {b.logo ? <img src={cloudyLogo(b.logo)} alt="" className="w-full h-full object-cover" loading="lazy"/> : <Storefront size={16} weight="duotone"/>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate flex items-center gap-2">
                        {b.name}
                        {isPremium && <span className="text-[9px] uppercase tracking-widest bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm font-bold">★</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{b.category_label || b.category} · {b.city}</div>
                    </div>
                    <ArrowRight size={14} className="text-muted-foreground shrink-0"/>
                  </button>
                );
              })}
            </>
          )}

          {cities.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold px-4 pt-3 pb-1.5 border-t border-border">Villes</div>
              {cities.map((c) => (
                <button
                  key={`city-${c.city}`}
                  onClick={() => goCity(c.city)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-accent/40 border-b border-border/50"
                  data-testid={`autocomplete-city-${c.city}`}
                >
                  <div className="w-7 h-7 shrink-0 bg-secondary/15 text-secondary grid place-items-center rounded-sm"><MapPin size={14}/></div>
                  <div className="text-sm flex-1"><b>{c.city}</b> <span className="text-muted-foreground">— {c.count} entreprise{c.count > 1 ? "s" : ""}</span></div>
                  <ArrowRight size={12} className="text-muted-foreground shrink-0"/>
                </button>
              ))}
            </>
          )}

          {categories.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold px-4 pt-3 pb-1.5 border-t border-border">Catégories</div>
              {categories.map((c) => (
                <button
                  key={`cat-${c.value || c.category}`}
                  onClick={() => goCategory(c.category)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-accent/40 border-b border-border/50"
                  data-testid={`autocomplete-cat-${c.value}`}
                >
                  <div className="w-7 h-7 shrink-0 bg-primary/10 text-primary grid place-items-center rounded-sm"><Tag size={14}/></div>
                  <div className="text-sm flex-1"><b>{c.category}</b> <span className="text-muted-foreground">— {c.count}</span></div>
                  <ArrowRight size={12} className="text-muted-foreground shrink-0"/>
                </button>
              ))}
            </>
          )}

          {q.trim() && (
            <button
              onClick={() => { setOpen(false); navigate(`${fallbackPath}?q=${encodeURIComponent(q.trim())}`); }}
              className="w-full text-left px-4 py-3 text-sm font-semibold text-primary hover:bg-accent/40 flex items-center justify-between border-t border-border bg-accent/20"
              data-testid="autocomplete-see-all"
            >
              Voir tous les résultats pour « {q.trim()} »
              <ArrowRight size={14}/>
            </button>
          )}
        </div>
      )}
      {open && !hasAny && q.trim().length >= 2 && !loading && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-card border border-border rounded-sm p-4 text-sm text-muted-foreground" data-testid="autocomplete-empty">
          Aucun résultat pour « {q.trim()} ». <button className="text-primary underline" onClick={() => navigate(`${fallbackPath}?q=${encodeURIComponent(q.trim())}`)}>Voir l'annuaire complet</button>
        </div>
      )}
    </div>
  );
}
